import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { Combobox } from "@headlessui/react";
import { setUserLocation } from "../Services/locationApi";

const LocationBar = ({ onSearch, searchQuery, refreshListings, terminalView, setLocationPopup, citisList, setLocationSuccess }) => {
    const { t } = useTranslation();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [isDistanceDropdownOpen, setIsDistanceDropdownOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [suggestions, setSuggestions] = useState([]);
    const [selectedDistance, setSelectedDistance] = useState("+ 5 km");
    const dropdownRef = useRef(null);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const options = [
        "+ 5 km",
        "+ 10 km",
        "+ 20 km",
        "+ 30 km",
        "+ 50 km",
        "+ 100 km",
        "+ 150 km",
        "+ 200 km",
    ];

    useEffect(() => {
        if (query.length < 3) {
            setSuggestions([]);
            return;
        }

        const fetchData = async () => {
            try {
                const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${query}`);
                const data = await response.json();

                if (Array.isArray(data) && data.length > 0) {
                    const validSuggestions = data.filter((place) => place.lat && place.lon);
                    setSuggestions(validSuggestions);
                } else {
                    setSuggestions([]);
                }
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        };

        fetchData();
    }, [query]);

    const toggleDropdown = () => {
        setIsDistanceDropdownOpen(!isDistanceDropdownOpen);
    };

    useEffect(() => {
        const handleOutsideClick = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDistanceDropdownOpen(false);
            }
        };

        document.addEventListener("mousedown", handleOutsideClick);
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
        };
    }, []);

    const selectOption = (option) => {
        setSelectedDistance(option);
        setIsDistanceDropdownOpen(false);
    };

    useEffect(() => {
        setSearchTerm(searchQuery);
    }, [searchQuery]);

    const handleChange = (event) => {
        setSearchTerm(event.target.value);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        onSearch(searchTerm);
        setIsModalOpen(false);

        if (!selectedLocation) {
            console.error("No location selected!");
            return;
        }

        const radius = parseInt(selectedDistance.replace("+ ", "").replace(" km", "")) || 5;

        try {
            const accessToken =
                window.localStorage.getItem("accessToken") ||
                window.sessionStorage.getItem("accessToken");
            if (
                !(
                    selectedLocation &&
                    selectedLocation.name &&
                    citisList &&
                    citisList.some(
                        city =>
                            city.name &&
                            city.name.toLowerCase() === selectedLocation.name.toLowerCase()
                    )
                )
            ) {
                setLocationPopup(selectedLocation);
                return;
            }
            if (accessToken) {
                let cityId = null;
                for (const city of citisList) {
                    if (
                        city.name &&
                        city.name.toLowerCase() === selectedLocation.name.toLowerCase()
                    ) {
                        cityId = city.id;
                        break;
                    }
                }
                const res = await setUserLocation(selectedLocation.lat, selectedLocation.lon, radius, cityId);
                if(res.data?.status==="success"){
                    setLocationSuccess(selectedLocation)
                }
                refreshListings({
                    latitude: selectedLocation.lat,
                    longitude: selectedLocation.lon,
                    radius,
                });
            }
        } catch (error) {
            console.error("Failed to update user location:", error);
        }
    };

    if (terminalView) {
        return (
            <div className="w-full bg-white/20 backdrop-blur-md rounded-md shadow-md px-4 py-2">
                <form onSubmit={(e) => {
                    e.preventDefault();
                    onSearch(searchTerm);
                }} className="flex items-center p-2">
                    <svg className="w-4 h-4 text-white mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 4a7 7 0 100 14 7 7 0 000-14zM21 21l-4.35-4.35" />
                    </svg>
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder={t("whatAreYouLookingFor")}
                        className="bg-transparent flex-grow outline-none text-white placeholder-white text-sm h-8"
                    />
                    <button
                        type="submit"
                        className="bg-white/20 backdrop-blur-md rounded-md px-4 py-2 ml-3 text-sm"
                    >
                        {t("find")}
                    </button>
                </form>
            </div>
        );
    }

    return (
        <div>
            <div className={`hidden xl:flex justify-center px-3 py-2 rounded-md`}>
                <div className="bg-white/20 backdrop-blur-md rounded-md flex items-center shadow-[0_3px_10px_rgb(0,0,0,0.2)] px-4 py-2 w-full">

                    <div className="flex items-center flex-1 p-2 relative">
                        <form onSubmit={handleSubmit} className="flex items-center flex-1 p-1">
                            <svg className="w-4 h-4 text-white mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 4a7 7 0 100 14 7 7 0 000-14zM21 21l-4.35-4.35" />
                            </svg>
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={handleChange}
                                placeholder={t("whatAreYouLookingFor")}
                                className="bg-transparent flex-grow outline-none text-white placeholder-white text-sm h-8"
                            />
                        </form>
                    </div>

                    <div className="border-l border-white h-full mx-2"></div>

                    <div className="flex items-center flex-1 p-2 relative">
                        <svg className="w-4 h-4 text-white mr-1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512" fill="currentColor">
                            <path d="M215.7 499.2C267 435 384 279.4 384 192C384 86 298 0 192 0S0 86 0 192c0 87.4 117 243 168.3 307.2c12.3 15.3 35.1 15.3 47.4 0zM192 128a64 64 0 1 1 0 128 64 64 0 1 1 0-128z" />
                        </svg>
                        <Combobox value={selectedLocation ? selectedLocation.display_name : query} onChange={setSelectedLocation}>
                            <Combobox.Input
                                onChange={(event) => setQuery(event.target.value)}
                                placeholder={t("chooseLocation")}
                                className="bg-transparent flex-grow outline-none text-white placeholder-white text-sm h-8"
                            />
                            {suggestions.length > 0 && (
                                <Combobox.Options ref={dropdownRef}
                                    className="absolute top-full left-0 mt-4 max-h-60 overflow-y-auto w-32 bg-white shadow-md rounded-lg z-10">
                                    {suggestions.map((place) => (
                                        <Combobox.Option
                                            key={place.place_id}
                                            value={place}
                                            onClick={() => {
                                                setQuery(place.display_name);
                                                setSelectedLocation(place);
                                            }}
                                            className={({ active }) =>
                                                `px-3 py-1 text-gray-700 cursor-pointer ${active ? "bg-gray-100" : ""}`
                                            }
                                        >
                                            {place.display_name}
                                        </Combobox.Option>
                                    ))}
                                </Combobox.Options>
                            )}
                        </Combobox>
                    </div>

                    <div className="border-l border-white h-full mx-2"></div>

                    <div className="relative flex-1 p-2">
                        <button className="text-white flex items-center justify-between w-full text-sm" onClick={toggleDropdown}>
                            <span className="mr-1">{selectedDistance}</span>
                            <svg className="w-3 h-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>

                        {isDistanceDropdownOpen && (
                            <div ref={dropdownRef}
                                className="absolute top-full left-0 mt-4 max-h-60 overflow-y-auto w-32 bg-white shadow-md rounded-lg z-10">
                                {options.map((option, index) => (
                                    <div
                                        key={index}
                                        onClick={() => selectOption(option)}
                                        className="px-3 py-1 text-gray-700 hover:bg-gray-100 cursor-pointer"
                                    >
                                        {option}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <button
                        className={`bg-white/20 backdrop-blur-md rounded-md px-4 py-2 ml-3 text-sm`}
                        style={{ fontFamily: "'Space Grotesk', Helvetica, Arial, Lucida, sans-serif" }}
                        onClick={handleSubmit}
                    >
                        {t("find")}
                    </button>
                </div>
            </div>

            <div className={`flex xl:hidden justify-center`}>
                <div className="bg-white/20 backdrop-blur-md rounded-md flex items-center shadow-md px-4 py-2 w-full">
                    <button
                        onClick={() => setIsModalOpen(true)}
                        className="flex items-center justify-between flex-1 p-2"
                    >
                        <span className="text-white truncate">{t("whereAndWhat")}</span>
                        <svg
                            className="w-4 h-4text-gray-500 mr-2"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M11 4a7 7 0 100 14 7 7 0 000-14zM21 21l-4.35-4.35"
                            />
                        </svg>
                    </button>
                </div>
            </div>

            {isModalOpen && (
                <div className="fixed inset-0 bg-black/20 backdrop-blur-sm flex justify-center items-center px-5 md:px-10 lg:px-[5rem] xl:px-[10rem] 2xl:px-[20rem] z-50">
                    <div className="bg-white rounded-lg shadow-lg p-6 w-11/12 md:w-2/3">
                        {/* Close Button */}
                        <button className="group absolute top-3 right-3 text-white text-lg w-12 h-12 bg-white/20 backdrop-blur-md border borderwhite outline-none flex items-center justify-center"
                            onClick={() => setIsModalOpen(false)}
                        >
                            <span className="absolute w-2 h-[1px] bg-current transition-all duration-200 ease-[cubic-bezier(.25,.46,.45,.94)] rotate-[-45deg] group-hover:w-[15px]"></span>
                            <span className="absolute w-2 h-[1px] bg-current transition-all duration-200 ease-[cubic-bezier(.25,.46,.45,.94)] rotate-[45deg] group-hover:w-[15px]"></span>
                        </button>

                        {/* Search Input */}
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder={t("whatAreYouLookingFor")}
                            className="w-full border border-gray-300 rounded-md p-3 mb-4 text-gray-700"
                        />

                        {/* Location Search */}
                        <div ref={dropdownRef}>
                            <Combobox
                                value={selectedLocation ? selectedLocation.display_name : query}
                                onChange={(place) => {

                                    if (place && place.display_name) {
                                        setQuery(place.display_name);
                                        setSelectedLocation(place);
                                    } else {
                                        console.error("Invalid place object:", place);
                                    }
                                }}
                            >
                                <Combobox.Input
                                    onChange={(event) => setQuery(event.target.value)}
                                    placeholder={t("chooseLocation")}
                                    className="w-full border border-gray-300 rounded-md p-3 mb-4 text-gray-700"
                                />
                                {suggestions.length > 0 && (
                                    <Combobox.Options className="max-h-40 overflow-y-auto border border-gray-300 rounded-md p-2 mb-4">
                                        {suggestions.map((place) => (
                                            <Combobox.Option
                                                key={place.place_id}
                                                value={place}
                                                className={({ active }) =>
                                                    `px-4 py-2 text-gray-900 cursor-pointer ${active ? "bg-gray-100" : ""}`
                                                }
                                            >
                                                {place.display_name}
                                            </Combobox.Option>
                                        ))}
                                    </Combobox.Options>
                                )}
                            </Combobox>
                        </div>

                        {/* Distance Dropdown */}
                        <div ref={dropdownRef}>
                            <button
                                onClick={() => setIsDistanceDropdownOpen(!isDistanceDropdownOpen)}
                                className="w-full text-gray-700 text-start border border-gray-300 rounded-md p-3 mb-4"
                            >
                                {selectedDistance}
                            </button>
                            {isDistanceDropdownOpen && (
                                <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-md p-2 mb-4">
                                    {options.map((option, index) => (
                                        <div
                                            key={index}
                                            onClick={() => {
                                                setSelectedDistance(option);
                                                setIsDistanceDropdownOpen(false);
                                            }}
                                            className="px-4 py-2 text-gray-700 hover:bg-gray-100 cursor-pointer"
                                        >
                                            {option}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Find Button */}
                        <button onClick={handleSubmit} className="bg-gradient-to-r from-gray-700 to-slate-600 font-bold text-white rounded-md px-4 py-2 w-full">
                            {t("find")}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

LocationBar.propTypes = {
    onSearch: PropTypes.func.isRequired,
    searchQuery: PropTypes.string,
    refreshListings: PropTypes.func.isRequired,
    terminalView: PropTypes.bool,
};

export default LocationBar;